import { PageController } from '@client/components/_core/layout/PageController';
import { Loading } from '@client/components/_ui/feedback/Loading';
// Static on purpose: autoCodeSplitting already route-scopes this component, so a nested lazy() only adds a second
// network hop after the route chunk lands (measured 227–1,230 ms of dead time in production traces).
import { IncidentForm } from '@client/components/incidents/incident-form/IncidentForm';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import {
    buildFormDraftDataType,
    ORG_FLEX_STORAGE_DATA_TYPES,
    resolveFormDraftSource,
} from '@shared/constants/system/org-flex-storage';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Card } from 'antd';
import type { PropsWithChildren } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/console/$orgId/compliance/incidents/add')({
    staticData: {
        metadata: {
            title: 'New incident',
        },
        ui: {
            title: 'New incident',
            widthVariant: 'compact',
            // Wider than the compact default (w-4xl) so the description
            // textarea and the paired timeline fields get breathing room.
            className: 'w-6xl max-w-full',
        },
    },
    validateSearch: zodValidator(
        z.object({
            messageClassificationId: z.coerce.bigint().optional().catch(undefined),
            // Set when logging an incident from a compliance audit finding
            // (CUR-168 / REQ-FND-7) — links the new incident back to the finding.
            complianceAuditFindingId: z.coerce.bigint().optional().catch(undefined),
            complaint: z
                .object({
                    complaintId: z.coerce.bigint().optional().catch(undefined),
                    handlingPartyIds: z.array(z.coerce.bigint()).optional().catch(undefined),
                    ownerId: z.coerce.bigint().optional().catch(undefined),
                    products: z.array(z.coerce.number()).optional().catch(undefined),
                    policyNo: z.string().optional().catch(undefined),
                    externalClaimId: z.string().optional().catch(undefined),
                })
                .strict()
                .optional()
                .catch(undefined),
        }),
    ),
    pendingMs: 0,
    pendingMinMs: 0,
    pendingComponent: PendingComponent,
    component: RouteComponent,
});

function PendingComponent() {
    return (
        <IncidentFormShell>
            <Loading className="min-h-[360px] py-12" />
        </IncidentFormShell>
    );
}

function IncidentFormShell({ children }: PropsWithChildren) {
    return (
        <PageController route={Route}>
            <Card>{children}</Card>
        </PageController>
    );
}

function RouteComponent() {
    const orgId = useOrgId();
    const { messageClassificationId, complianceAuditFindingId, complaint } = Route.useSearch();
    const navigate = useNavigate();
    // CUR-1825: the router keeps this component mounted when only the search params
    // change, so key the form by its draft key. A new source then remounts the form
    // and hydrates that source's draft instead of autosaving the old values under it.
    const draftKey = buildFormDraftDataType(
        ORG_FLEX_STORAGE_DATA_TYPES.INCIDENT_DRAFT,
        resolveFormDraftSource('incident', {
            complianceAuditFindingId,
            messageClassificationId,
            complaintId: complaint?.complaintId,
        }),
    );

    return (
        <IncidentFormShell>
            <IncidentForm
                key={draftKey}
                messageClassificationId={messageClassificationId}
                complianceAuditFindingId={complianceAuditFindingId}
                complaintData={complaint}
                onCancel={() => navigate({ to: '/console/$orgId/compliance/incidents', params: { orgId } })}
                onAfterSaveAccessDenied={() =>
                    navigate({ to: '/console/$orgId/compliance/incidents', params: { orgId } })
                }
                onAfterSaveCancel={({ id }) =>
                    navigate({
                        to: '/console/$orgId/compliance/incidents/details/$id/manage',
                        params: { orgId, id },
                    })
                }
            />
        </IncidentFormShell>
    );
}
